import { useCallback, useContext, useEffect, useMemo } from 'react';
import type { IconName } from '@launchpad-ui/icons';
import { DataContext } from '@visx/xychart';

import { getChartTypeConfig } from '../../chartTypes';
import { TOOLTIP_SNAP_DISTANCE_PX } from '../../constants';
import type { ChartDataPoint } from '../../types';
import type { ReferenceLineMetadata } from '../../types/ChartContext';
import type { XScaleType } from '../../types/Scale';
import type { TooltipComponentProps, TooltipSeriesItem } from '../../types/TooltipProps';
import { findClosestRefLineOrDataPoint } from '../../utils/findClosestRefLineOrDataPoint';
import { type DataType, formatDateTime, getStandaloneFormatterForTooltip } from '../../utils/formatters';
import { getSeriesValuesAtX } from '../../utils/getSeriesValuesAtX';
import { createSnapStrategy } from '../../utils/tooltipSnap';
import { useChartContext } from '../../utils/useChartContext';
import { useSvgCursorPosition } from '../../utils/useSvgCursorPosition';
import { useSyncedZoomContext } from '../SyncedChartProvider';

import { useTooltipContext } from './TooltipContext';

interface UseTooltipContentParams {
  enabled: boolean;
  xAxisLabelFormatter?: (value: number) => string;
  xAxisDataType?: XScaleType;
  /** Data type for automatic domain-aware Y-axis formatting */
  yAxisDataType?: DataType;
  /** Custom formatter - takes precedence over yAxisDataType. Optional second arg receives the full series item. */
  yAxisValueFormatter?: (value: number, item?: TooltipSeriesItem) => string;
  renderTooltipSecondaryRow?: (dataPoint: ChartDataPoint, dataKey?: string) => React.ReactNode;
  /** Optional footer content rendered below all series and event groups */
  renderTooltipFooter?: (series: TooltipSeriesItem[]) => React.ReactNode;
  /** Whether to sort tooltip series by value (largest first) */
  sortSeriesByValue?: boolean;
  /** Whether click-to-freeze is enabled - affects cursor tracking when pinned */
  enableClickToFreeze?: boolean;
  /** The tooltip component to render (already resolved to DefaultTooltip if not provided) */
  tooltipContentComponent: React.ComponentType<TooltipComponentProps>;
  /** Default icon type for tooltip row indicators */
  rowIcon?: IconName;
}

export interface TooltipContentResult {
  content: React.ReactNode;
  /** X position for crosshair (null if crosshair should not be shown, e.g., ColumnChart bars) */
  snappedXPosition: number | null;
  /** X position of the data point (always set when hovering over valid data, used for pinning) */
  dataPointXPosition: number | null;
  /** The data x-value at the current tooltip position (used for pinning) - can be number or string for categories */
  activeXValue: number | string | null;
}

/**
 * Format a number without unnecessary decimal places.
 * If the number is a whole number, don't show decimals.
 * Otherwise, show up to 2 decimal places.
 */
function formatNumberWithoutUnnecessaryDecimals(value: number): string {
  // Check if the number is a whole number (no decimal part)
  if (Number.isInteger(value)) {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  // For numbers with decimals, show up to 2 decimal places
  return value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * Hook that handles all tooltip logic: finding nearest data points,
 * formatting labels, and rendering appropriate tooltip content.
 *
 * Reference line content is rendered WITHIN the default tooltip
 * (at the bottom) when hovering near a reference line.
 *
 * When tooltip is pinned (click-to-freeze feature), returns the stored pinned data
 * instead of computing from cursor position.
 *
 * @returns Object with tooltip content, snapped X position for crosshair, and active X value
 */
export function useTooltipContent({
  enabled,
  xAxisLabelFormatter,
  xAxisDataType: providedXAxisDataType,
  yAxisDataType,
  yAxisValueFormatter,
  renderTooltipSecondaryRow,
  renderTooltipFooter,
  sortSeriesByValue = false,
  enableClickToFreeze = false,
  tooltipContentComponent: TooltipContentComponent,
  rowIcon,
}: UseTooltipContentParams): TooltipContentResult {
  const { xDataKey, seriesDisplayMetadata, referenceLines, chartType, xScaleType, xDataType, zoomDomain, chartData } =
    useChartContext();
  // Use xDataType (format type) for formatting, not xScaleType (positioning type)
  // This ensures ColumnChart with time data formats timestamps as dates even though scale is 'band'
  const xAxisDataType = providedXAxisDataType ?? xDataType;
  const {
    setActiveXValue,
    isPinned,
    pinnedXPosition,
    pinnedXValue,
    pinnedTooltipContent,
    unpinTooltip,
    isDirectlyHovered,
  } = useTooltipContext();
  const syncedZoom = useSyncedZoomContext();
  const dataContext = useContext(DataContext);

  // When pinned, don't track cursor position (disable cursor tracking)
  const shouldTrackCursor = enabled && !(enableClickToFreeze && isPinned);
  const { cursorX: cursorSvgX } = useSvgCursorPosition(shouldTrackCursor);

  // Create snap strategy based on scale type (memoized to avoid recreation on every render)
  // Crosshair behavior comes from chart type config
  const snapStrategy = useMemo(() => {
    if (!dataContext?.xScale) {
      return null;
    }
    const { showCrosshairOnDataPoints } = getChartTypeConfig(chartType);
    return createSnapStrategy(xScaleType, dataContext.xScale, showCrosshairOnDataPoints);
  }, [chartType, xScaleType, dataContext?.xScale]);

  const formatXAxisLabel = useCallback(
    (value: number | string): string => {
      // For string values (categorical data), return as-is
      if (typeof value === 'string') {
        return value;
      }

      if (xAxisLabelFormatter) {
        return xAxisLabelFormatter(value);
      }

      // Format based on xDataType (format type) from context
      if (xAxisDataType === 'time') {
        return formatDateTime(value);
      }

      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    },
    [xAxisDataType, xAxisLabelFormatter],
  );

  /**
   * Creates a Y-axis value formatter for the current tooltip.
   * Priority:
   * 1. Custom yAxisValueFormatter (full control)
   * 2. Per-value standalone formatter when yAxisDataType is set (e.g. 5K not 0.005M)
   * 3. Default number formatting
   */
  const createYAxisFormatter = useCallback(
    (_seriesValues: number[]): ((value: number) => string) => {
      // Custom formatter takes precedence
      if (yAxisValueFormatter) {
        return yAxisValueFormatter;
      }

      // Per-value formatting when dataType is specified (tooltip shows each value in its own magnitude)
      if (yAxisDataType) {
        return getStandaloneFormatterForTooltip(yAxisDataType);
      }

      // Default fallback
      return formatNumberWithoutUnnecessaryDecimals;
    },
    [yAxisValueFormatter, yAxisDataType],
  );

  /**
   * Extracts event groups from reference lines for display in the tooltip.
   * Consolidates reference lines only if they have the same tooltipName AND exact same position.
   * Returns an array of event groups (tooltipName + events array).
   */
  const extractEventGroups = useCallback(
    (
      nearbyReferenceLines: ReferenceLineMetadata[],
    ): Array<{ tooltipName?: string; events: Array<{ seriesKey?: string; eventDescription: string }> }> => {
      // Group events by (tooltipName + point) to consolidate only exact same position + name
      const groupedByNameAndPosition = new Map<
        string,
        { tooltipName?: string; events: Array<{ seriesKey?: string; eventDescription: string }> }
      >();

      for (const refLine of nearbyReferenceLines) {
        // Skip if no tooltipName and no events
        if (!refLine.tooltipName && (!refLine.events || refLine.events.length === 0)) {
          continue;
        }

        // Create a composite key from name and position
        const key = `${refLine.tooltipName ?? ''}_${refLine.point}`;
        const existing = groupedByNameAndPosition.get(key);

        if (existing) {
          // Merge events into existing group
          existing.events.push(...(refLine.events ?? []));
        } else {
          // Create new group
          groupedByNameAndPosition.set(key, {
            tooltipName: refLine.tooltipName,
            events: [...(refLine.events ?? [])],
          });
        }
      }

      // Convert map to array
      return Array.from(groupedByNameAndPosition.values());
    },
    [],
  );

  /**
   * Creates a map of series keys to their colors for use in event display.
   */
  const seriesColorMap = useMemo((): Map<string, string> => {
    const colorMap = new Map<string, string>();
    seriesDisplayMetadata.forEach((metadata, key) => {
      colorMap.set(key, metadata.color);
    });
    return colorMap;
  }, [seriesDisplayMetadata]);

  /**
   * Renders the complete tooltip with series data and optional reference line events.
   * Creates a domain-aware formatter from the current series values for consistent display.
   */
  const renderTooltip = useCallback(
    (
      seriesData: TooltipSeriesItem[],
      label: number | string,
      nearbyReferenceLines: ReferenceLineMetadata[],
    ): React.ReactNode => {
      const formattedLabel = formatXAxisLabel(label);
      const eventGroups = extractEventGroups(nearbyReferenceLines);

      const orderedSeries = sortSeriesByValue ? [...seriesData].sort((a, b) => b.value - a.value) : seriesData;

      // Extract all values from current series for domain-aware formatting
      const seriesValues = orderedSeries.map((s) => s.value);
      const yAxisFormatter = createYAxisFormatter(seriesValues);

      return (
        <TooltipContentComponent
          label={formattedLabel}
          series={orderedSeries}
          seriesColorMap={seriesColorMap}
          yAxisValueFormatter={yAxisFormatter}
          renderTooltipSecondaryRow={renderTooltipSecondaryRow}
          renderTooltipFooter={renderTooltipFooter}
          eventGroups={eventGroups.length > 0 ? eventGroups : undefined}
          rowIcon={rowIcon}
        />
      );
    },
    [
      formatXAxisLabel,
      extractEventGroups,
      seriesColorMap,
      createYAxisFormatter,
      renderTooltipSecondaryRow,
      renderTooltipFooter,
      TooltipContentComponent,
      rowIcon,
      sortSeriesByValue,
    ],
  );

  // When pinned, return the stored pinned data instead of computing from cursor
  const pinnedData = useMemo((): TooltipContentResult | null => {
    if (!enableClickToFreeze || !isPinned) {
      return null;
    }

    // Return stored pinned data
    return {
      content: pinnedTooltipContent,
      snappedXPosition: pinnedXPosition,
      dataPointXPosition: pinnedXPosition, // When pinned, both are the same
      activeXValue: pinnedXValue,
    };
  }, [enableClickToFreeze, isPinned, pinnedTooltipContent, pinnedXPosition, pinnedXValue]);

  // Compute tooltip data from cursor position (only when not pinned)
  const computedData = useMemo((): TooltipContentResult => {
    // If pinned, don't compute from cursor (pinnedData will be used instead)
    if (pinnedData) {
      return pinnedData;
    }

    // Check for nearby data points and reference lines
    if (!dataContext?.xScale || cursorSvgX === null || !snapStrategy || !chartData) {
      return { content: null, snappedXPosition: null, dataPointXPosition: null, activeXValue: null };
    }

    // Find closest target using the strategy pattern
    const closestTarget = findClosestRefLineOrDataPoint(
      cursorSvgX,
      chartData,
      referenceLines,
      xDataKey,
      dataContext.xScale as (value: number) => number | undefined,
      TOOLTIP_SNAP_DISTANCE_PX,
      snapStrategy,
      xScaleType,
      zoomDomain,
    );

    if (!closestTarget) {
      return { content: null, snappedXPosition: null, dataPointXPosition: null, activeXValue: null };
    }

    // Get series data at the x position (empty array if no data point nearby)
    const seriesData = closestTarget.hasDataPoint
      ? getSeriesValuesAtX(closestTarget.xValue, chartData, seriesDisplayMetadata, xDataKey, rowIcon)
      : [];

    // Use strategy to determine if crosshair should be shown
    const hasNearbyReferenceLine = closestTarget.nearbyReferenceLines.length > 0;
    let showCrosshair = snapStrategy.shouldShowCrosshair(closestTarget.hasDataPoint, hasNearbyReferenceLine);
    // When inside a synced chart group, show crosshair on non-hovered charts so ColumnChart shows the synced line
    if (syncedZoom !== null && !isDirectlyHovered) {
      showCrosshair = true;
    }

    // Don't show tooltip if there's no actual data (all series are null) and no reference lines
    // But still show crosshair at the cursor position
    if (seriesData.length === 0 && !hasNearbyReferenceLine) {
      return {
        content: null,
        snappedXPosition: showCrosshair ? closestTarget.xPosition : null,
        dataPointXPosition: closestTarget.xPosition,
        activeXValue: closestTarget.xValue,
      };
    }

    // Render tooltip with series data + reference line content
    return {
      content: renderTooltip(seriesData, closestTarget.xValue, closestTarget.nearbyReferenceLines),
      snappedXPosition: showCrosshair ? closestTarget.xPosition : null,
      // dataPointXPosition is always set when we have a data point (used for pinning)
      dataPointXPosition: closestTarget.xPosition,
      activeXValue: closestTarget.xValue,
    };
  }, [
    pinnedData,
    dataContext,
    cursorSvgX,
    seriesDisplayMetadata,
    referenceLines,
    xDataKey,
    snapStrategy,
    chartData,
    renderTooltip,
    xScaleType,
    zoomDomain,
    rowIcon,
    syncedZoom,
    isDirectlyHovered,
  ]);

  // Update active X value state (side effect in useEffect, not in useMemo)
  // When pinned, use the pinned X value
  useEffect(() => {
    setActiveXValue(computedData.activeXValue);
  }, [computedData.activeXValue, setActiveXValue]);

  // Auto-unpin tooltip when zoom domain changes
  // This avoids complex recalculation of tooltip position after zoom
  useEffect(() => {
    if (isPinned) {
      unpinTooltip();
    }
  }, [zoomDomain]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only react to zoomDomain changes

  return computedData;
}
